import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CreateLeadCandidateInput, UpdateLeadCandidateInput } from "@school-sales-ai/shared";
import {
  createLeadCandidateApi,
  removeLeadCandidateApi,
  updateLeadCandidateApi,
} from "@/lib/api";

/**
 * Mutations over the people a lead is enquiring for. No query hook: the list
 * arrives inside the lead-detail payload.
 *
 * Assigning a program to a candidate registers the ordinary interest too, and
 * that can promote `leads.product_id` — the same structural change
 * `useLeadInterests` invalidates the board and the product facet for. Kept in
 * step with that hook deliberately: the two write the same downstream state.
 */
function useCandidateMutation<TArgs>(
  leadId: string,
  fn: (args: TArgs) => Promise<void>,
  successMessage: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline-stage"] });
      void queryClient.invalidateQueries({ queryKey: ["product-counts"] });
      toast.success(successMessage);
    },
    onError: (err: Error) => {
      toast.error(err.message || "No se pudo actualizar la información");
    },
  });
}

export function useCreateLeadCandidate(leadId: string) {
  return useCandidateMutation<CreateLeadCandidateInput>(
    leadId,
    (input) => createLeadCandidateApi(leadId, input),
    "Persona agregada",
  );
}

export function useUpdateLeadCandidate(leadId: string) {
  return useCandidateMutation<{ candidateId: string; input: UpdateLeadCandidateInput }>(
    leadId,
    ({ candidateId, input }) => updateLeadCandidateApi(leadId, candidateId, input),
    "Datos actualizados",
  );
}

export function useRemoveLeadCandidate(leadId: string) {
  return useCandidateMutation<string>(
    leadId,
    (candidateId) => removeLeadCandidateApi(leadId, candidateId),
    "Persona eliminada",
  );
}
